import {Injectable} from '@angular/core';
import {environment} from '@Env/environment';
import {EntiteTreeNode, EntiteTreeNodeDump} from './entite-tree-node.model';
import {HttpClient} from '@angular/common/http';
import {tap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {Entite} from "@Services/gestion-universite/entite/entite.model";


@Injectable({
  providedIn: 'root'
})
export class EntiteTreeNodeService {
  private url = `${environment.baseUrl}/gestion_universite/entite-tree-node/`;

  constructor(private http: HttpClient) {
  }

  create(entiteTreeNode: EntiteTreeNode) {
    return this.http.post<EntiteTreeNode>(this.url, entiteTreeNode).pipe(tap(data => this.list()));
  }

  list() {
    return this.http.get<EntiteTreeNode[]>(this.url);
  }

  /**
   * Retourne l'arborescence depuis la racine Université (path='0014', mangue='1')
   * Il n'y a qu'une seule entrée dans le tableau
   */
  listDump() {
    return this.http.get<EntiteTreeNodeDump[]>(`${this.url}extended_dump/`);
  }

  addToParent(entite: Entite, parentId: number) {
    return this.http.post<any>(`${this.url}extended_add/`, {entite: entite, parent_id: parentId});
  }

  moveToTargetPos(dragNodeId: number, targetNodeId: number, pos: "left" | "right" | "last-child") {
    return this.http.put<any>(`${this.url}extended_move/`, {src: dragNodeId, tgt: targetNodeId, pos: pos});
  }

  /**
   * Liste d'entités selon leur profondeur et optionnellement leur(s) type(s)
   * @param {number[]} depths : les profondeurs souhaitées, par ex : 2, 2-4, 3, 3-4, etc
   * @param {string[]} types : option, permet de réduire aux types, par ex: cmp:dep, cmp:adm, adm, etc
   */
  listByDepth(depths: number[], types: string[] = []) {
    const depths_url = depths.reduce(function (acc, currentValue, idx) {
      return idx === 0 ? currentValue : `${acc}-${currentValue}`;
    }, '');
    const types_url = types.reduce(function (acc, currentValue, idx) {
      return idx === 0 ? currentValue : `${acc}:${currentValue}`;
    }, '');
    const args = types_url ? `${depths_url}:${types_url}` : `${depths_url}`;
    return this.http.get<EntiteTreeNode[]>(`${this.url}?by_depth=${args}&ordering=name`);
  }

  listByType(type: string) {
    return this.http.get<EntiteTreeNode[]>(`${this.url}?type_entite=${type}`);
  }

  private _getValueByParent(entiteId: number, type) {
    let value = '';
    if (type) {
      value = `${entiteId}:${type}`;
    } else {
      value = `${entiteId}`;
    }
    return value;
  }

  /**
   * Liste des enfants directs (à 1 niveau) de l'entité donnée
   * @param {number} entiteId : entité à partir de laquelle sont ramenés ses enfants
   * @param {string} type : optionnel, types de recherche séparés par un : (ex. : cmp:adm)
   * @param {boolean} activated : optionnel, uniquement les entités actives ou inactives
   */
  listByParent(entiteId: number, type = null, activated: boolean = null) {
    const value = this._getValueByParent(entiteId, type);
    const params = {
      by_parent: value
    };
    if (activated !== null) {
      params['activated'] = activated;
    }
    return this.http.get<EntiteTreeNode[]>(this.url, {params: params});
  }

  /**
   * Liste de toute la descendance d'un noeud (sera exclu) de l'entitée donnée
   * @param {number} entiteId : entité à partir de laquelle sont ramenés ses enfants
   * @param {string} type : optionnel, types de recherche séparés par un : (ex. : cmp:adm)
   */
  listDescendantsByParent(entiteId: number, type = null) {
    const value = this._getValueByParent(entiteId, type);
    return this.http.get<EntiteTreeNode[]>(this.url, {params: {descendants_by_parent: value}});
  }

  /**
   * Recherche de "mots" dans code ou name entité
   * @param {number} entiteId : entité à partir de laquelle la recherche s'effectue (arbo en-dessous, hors celle-ci donc)
   * @param {string} keyword : mots clés de recherche, la chaîne est décomposée en mot à mot avec l'espace
   * @param {string} types : optionnel, sur quelles types d'entités (types séparés par un :) s'opère la recherche (par ex. : cmp:adm, types possibles : cmp, adm, dep, lab, ...)
   * @param {number[]} depths : profondeurs de recherche des entités
   * @param {boolean} active: les entités actives ou non uniquemement, par défaut toutes
   */
  searchByParentTypes(entiteId: number, keyword = '',
                      types: string = null, depths: number[] = null,
                      active: boolean = null): Observable<EntiteTreeNode[]> {
    let value = '';
    if (types) {
      value = `${entiteId}||${keyword}||${types}`;
      if (depths) {
        const depths_url = depths.reduce(function (acc, currentValue, idx) {
          return idx === 0 ? currentValue : `${acc}:${currentValue}`;
        }, '');
        value = `${value}||${depths_url}`;
      }
    } else {
      value = `${entiteId}||${keyword}`;
    }
    let params: {};
    if (active !== null) {
      params = {by_search: value, activated: active};
    } else {
      params = {by_search: value};
    }

    return this.http.get<EntiteTreeNode[]>(this.url, {params: params});
  }

  get(id: number) {
    return this.http.get<EntiteTreeNode>(`${this.url}${id}/`);
  }

  update(entiteTreeNode: EntiteTreeNode) {
    return this.http.put<EntiteTreeNode>(`${this.url}${entiteTreeNode.id}/`, entiteTreeNode);
  }

  delete(entiteTreeNode: EntiteTreeNode) {
    return this.http.delete<EntiteTreeNode>(`${this.url}${entiteTreeNode.id}/`);
  }
}
